import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BehaviorSubject, Observable, combineLatest, map, of, switchMap } from 'rxjs';
import { AwbLookUpService, ConfigService, CustomerService, SessionService } from 'src/app/shared/services';
import { FeatureRestrictionsService } from 'src/app/shared/services/features/feature-restrictions.service';
import { InformationCollectorByPaymentService } from '../../information-collector-by-payment/collector/services/information-collector-by-payment.service';
import { AutoPayFormGroupType } from '../auto-pay/models/auto-pay-form';
import { MAWBSubscriptionFormGroupType } from '../mawb-subscription/models/mawb-subscription-form';

@Injectable()
export class AwbSubscriptionService {
    private enableSubscription: BehaviorSubject<MAWBSubscriptionFormGroupType> =
        new BehaviorSubject<MAWBSubscriptionFormGroupType>({} as MAWBSubscriptionFormGroupType);
    private autoPayForm: BehaviorSubject<AutoPayFormGroupType> = new BehaviorSubject<AutoPayFormGroupType>(
        {} as AutoPayFormGroupType
    );
    private awbSubscription: string;
    private acceptedPolicy: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private selectedAutoPayFirst: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private isEnabledSubscriptionWithoutRCForNFD = false;
    private isArrivalSubscriptionExist = false;
    private isEnabledCargoCredit: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private isEnoughCargoCredit: BehaviorSubject<boolean> = new BehaviorSubject(false);

    constructor(
        private matSnackBar: MatSnackBar,
        private configService: ConfigService,
        private awbLookUpService: AwbLookUpService,
        private featureRestrictionsService: FeatureRestrictionsService,
        private sessionService: SessionService,
        private informationCollectorByPaymentService: InformationCollectorByPaymentService,
        private customerService: CustomerService
    ) {}

    setIsEnabledSubscriptionWithoutRCForNFD(isEnabled: boolean): void {
        this.isEnabledSubscriptionWithoutRCForNFD = isEnabled;
    }

    getIsEnabledSubscriptionWithoutRCForNFD(): boolean {
        return this.isEnabledSubscriptionWithoutRCForNFD;
    }

    setIsArrivalSubscriptionExist(isEnabled: boolean): void {
        this.isArrivalSubscriptionExist = isEnabled;
    }

    getIsArrivalSubscriptionExist(): boolean {
        return !!this.isArrivalSubscriptionExist;
    }

    setSubscriptionForm(isEnableSubscription: MAWBSubscriptionFormGroupType): void {
        this.enableSubscription.next(isEnableSubscription);
    }

    getSubscriptionForm(): Observable<MAWBSubscriptionFormGroupType> {
        return this.enableSubscription.asObservable();
    }

    setAwbForSubscription(awb: string): void {
        this.awbSubscription = awb;
    }

    getAwbForSubscription(): string {
        return this.awbSubscription;
    }

    setAutoPayForm(autoPayForm: AutoPayFormGroupType): void {
        this.autoPayForm.next(autoPayForm);
    }

    getAutoPayForm(): Observable<AutoPayFormGroupType> {
        return this.autoPayForm?.asObservable();
    }

    setPolicy(isAcceptedPolicy: boolean): void {
        this.acceptedPolicy.next(isAcceptedPolicy);
    }

    getPolicy(): Observable<boolean> {
        return this.acceptedPolicy?.asObservable();
    }

    isPolicyAccepted(): Observable<boolean> {
        return this.getAutoPayForm().pipe(
            switchMap((autoPayForm: AutoPayFormGroupType) => {
                if (autoPayForm?.get('isSelected')?.value) {
                    return this.getPolicy();
                }
                return of(true);
            })
        );
    }

    isRequestorUser(): Observable<boolean> {
        return this.isCompany().pipe(
            switchMap((isCompany) => {
                if (isCompany) {
                    return this.isRequestor();
                }
                return of(false);
            })
        );
    }

    isAutoPayValidToContinue(): Observable<boolean> {
        return this.getAutoPayForm().pipe(
            switchMap((autoPayForm) => {
                if (!!autoPayForm && autoPayForm?.get('isSelected')?.value) {
                    return this.isEnabledContinueAutoPay().pipe(
                        switchMap((isEnableAutoPay) => {
                            if (!isEnableAutoPay) {
                                return of(isEnableAutoPay);
                            }
                            return this.isValidCustomerReference();
                        })
                    );
                }
                return of(true);
            })
        );
    }

    isSubscriptionValid(): Observable<boolean> {
        return this.getSubscriptionForm().pipe(
            map((subscriptionForm) => {
                return subscriptionForm?.valid;
            })
        );
    }

    isLeastOneOptionIsSelected(): Observable<boolean> {
        return combineLatest([this.getAutoPayForm(), this.getSubscriptionForm()]).pipe(
            map(([autoPayForm, subscriptionForm]) => {
                return !!(autoPayForm?.get('isSelected')?.value || subscriptionForm?.get('isSelected')?.value);
            })
        );
    }

    isAllowedToContinue(): Observable<boolean> {
        return combineLatest([
            this.isPolicyAccepted(),
            this.isAutoPayValidToContinue(),
            this.isSubscriptionValid(),
            this.isLeastOneOptionIsSelected(),
        ]).pipe(
            map(([isPolicyAccepted, isAutoPayValid, isSubscriptionValid, isLeastOneOptionIsSelected]) => {
                return !!(isPolicyAccepted && isSubscriptionValid && isAutoPayValid && isLeastOneOptionIsSelected);
            })
        );
    }

    selectAutoPayFirst(selectedAutoPayFirst: boolean): void {
        this.selectedAutoPayFirst.next(selectedAutoPayFirst);
    }

    get instant_is_select_auto_pay(): boolean {
        return this.selectedAutoPayFirst.value;
    }

    isSelectAutoPayFirst(): Observable<boolean> {
        return this.selectedAutoPayFirst.asObservable();
    }

    showTooltip(message: string): void {
        this.matSnackBar.open(message, 'Close', {
            duration: 5000,
            horizontalPosition: 'end',
        });
    }

    isSubscriptionAvailable(): boolean {
        if (!this.configService.isAllowedAWBSubscription()) {
            return false;
        }

        if (this.awbLookUpService.instant_current_payment?.isError) {
            return false;
        }

        if (this.awbLookUpService.instant_current_payment?.isAutoPaySubscriptionExist) {
            return true;
        }

        if (!this.awbLookUpService.instant_current_payment?.charges?.length) {
            return true;
        }

        return !(
            this.awbLookUpService.isAWBReceivedFromFlight() ||
            this.awbLookUpService.isAWBAgentNotifiedOfArrival() ||
            this.awbLookUpService.isAWBReceivedFromShipper()
        );
    }

    setEnabledCargoCredit(isEnabledCredit: boolean): void {
        this.isEnabledCargoCredit.next(isEnabledCredit);
    }

    isEnabledCredit(): Observable<boolean> {
        return this.isEnabledCargoCredit.asObservable();
    }

    setEnoughCargoCredit(enoughtCargoCredit: boolean): void {
        this.isEnoughCargoCredit.next(enoughtCargoCredit);
    }

    isEnoughCredit(): Observable<boolean> {
        return this.isEnoughCargoCredit.asObservable();
    }

    isAccountOnHold(): Observable<boolean> {
        return this.featureRestrictionsService.isAccountOnHold();
    }

    isAuthenticated(): Observable<boolean> {
        return this.sessionService.isAuthenticated$();
    }

    isValidCustomerReference(): Observable<boolean> {
        return this.informationCollectorByPaymentService.validateForms();
    }

    isRequestor(): Observable<boolean> {
        return of(this.customerService.isRequestor());
    }

    isCompany(): Observable<boolean> {
        return of(this.customerService.isCompanyUser() && !!this.customerService.getCompanyId());
    }

    isValidAutoPay(): Observable<boolean> {
        return this.getAutoPayForm().pipe(
            switchMap((autoPayForm) => {
                if (autoPayForm?.get('isSelected')?.value) {
                    return this.isEnabledContinueAutoPay();
                }
                return of(true);
            })
        );
    }

    isEnabledContinueAutoPay(): Observable<boolean> {
        return this.isAuthenticated().pipe(
            switchMap((isAuthenticated) => {
                if (!isAuthenticated) {
                    return of(false);
                }
                return this.isCompany().pipe(
                    switchMap((isCompany) => {
                        if (isCompany) {
                            return this.isRequestorUser();
                        }
                        return this.isEnabledCargoSprintCredit();
                    })
                );
            })
        );
    }

    isEnabledCargoSprintCredit(): Observable<boolean> {
        return this.isEnabledCredit().pipe(
            switchMap((isEnabledCargoCredit) => {
                if (!isEnabledCargoCredit) {
                    return of(false);
                }
                return this.isAccountOnHold().pipe(
                    switchMap((isAccountOnHold) => {
                        if (isAccountOnHold) {
                            return of(false);
                        }
                        return this.isEnoughCredit();
                    })
                );
            })
        );
    }
}
